/**
 * useLiveKitMasterBroadcast (web) — LiveKit publish engine for the master
 * broadcaster (CORE-393). Encapsulates the LiveKit-specific bits so
 * MasterLivePublisher can branch its RTC touchpoints to it when the live's
 * `rtcProvider === 'livekit'`, leaving the Agora path untouched.
 *
 * Flow mirrors Agora's: preview (getUserMedia, local self-view) → go-live
 * (connect to the room + publish the SAME preview tracks, so there's no camera
 * re-acquire / flicker) → mute toggles → end. The master's identity is the
 * server-provided token identity; the URL comes from the token too (nothing
 * baked in).
 *
 * Solo broadcast (camera + mic) — screen-share and cohost fan-out on LiveKit are
 * follow-ups (see docs/rtc-provider.md); they stay hidden on LiveKit for now.
 */
import { useCallback, useRef, useState } from 'react';
// CORE-1108 — классы из `loadLiveKit()`, типы стираются при сборке.
import type { LocalAudioTrack, LocalVideoTrack, Room } from 'livekit-client';
import { LkTrackSource, liveKitLoaded, loadLiveKit } from './liveKitLazy';

/**
 * CORE-737 — publication name that marks the library practice. Both sides match
 * on this string: the master publishes with it, viewers skip it. A LiveKit
 * `source` is not enough — ScreenShare already means something else, and Unknown
 * is what every unclassified track carries.
 */
export const PRACTICE_TRACK_NAME = 'agapella-practice';
import type { AgoraTokenResponse } from '../../types/auth';
import { api } from '../api';
import { applySelfViewMirror } from './selfViewMirror';

export interface LiveKitMasterBroadcast {
  /** Acquire camera+mic and show a local preview in `container`. */
  startPreview: (
    container: HTMLElement | null,
    opts?: { audioMode?: 'voice' | 'music'; micDeviceId?: string; camDeviceId?: string },
  ) => Promise<void>;
  /**
   * Connect to the room and publish the preview tracks. `onRoom` (if given) is
   * called with the fresh Room BEFORE connect, so callers can attach listeners
   * (e.g. cohost subscribe) and not miss participants already in the room.
   */
  goLive: (token: AgoraTokenResponse, opts?: { onRoom?: (room: Room) => void }) => Promise<void>;
  setMicEnabled: (on: boolean) => Promise<void>;
  setCamEnabled: (on: boolean) => Promise<void>;
  /** Attach the local camera into `container` (called on (re)mount of the self tile). */
  attachLocal: (container: HTMLElement | null) => void;
  /** Current mic input level 0..1 (for the level meter). */
  micLevelRef: React.MutableRefObject<number>;
  /** Whether a screen-share track is currently published. */
  isScreenSharing: boolean;
  /** In-flight flag for the screen-share toggle (mirrors useScreenShare). */
  screenShareLoading: boolean;
  /**
   * Publish/unpublish a screen-share track on the SAME room connection.
   * Unlike Agora (second client + separate UID/token), LiveKit publishes the
   * `getDisplayMedia` track from the host participant with
   * `source: LkTrackSource.ScreenShare`; the viewer's initLiveKit already routes
   * that source to the screen slot. Must be called after goLive (needs a room).
   */
  toggleScreenShare: () => Promise<void>;
  /** Graceful screen-share stop (unpublish + stop track + flip BE state). Safe when idle. */
  stopScreenShare: () => Promise<void>;
  /**
   * CORE-737 — publish the library practice into the SAME room so the egress
   * recording contains it. Named `PRACTICE_TRACK_NAME` and published with an
   * unspecified source: viewers match on that name and skip it, because they
   * already play their own copy of the clip in sync. Must be called after
   * goLive. Passing null tracks is a no-op.
   */
  publishPractice: (video: MediaStreamTrack | null, audio: MediaStreamTrack | null) => Promise<void>;
  /** CORE-737 — unpublish the practice tracks. Safe when nothing is published. */
  unpublishPractice: () => Promise<void>;
  /** Tear everything down (disconnect + stop local media). */
  stop: () => void;
}

export function useLiveKitMasterBroadcast(liveId?: number): LiveKitMasterBroadcast {
  const roomRef = useRef<Room | null>(null);
  const audioTrackRef = useRef<LocalAudioTrack | null>(null);
  const videoTrackRef = useRef<LocalVideoTrack | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const micLevelRef = useRef(0);
  const analyserCleanupRef = useRef<(() => void) | null>(null);
  const screenVideoTrackRef = useRef<LocalVideoTrack | null>(null);
  const screenAudioTrackRef = useRef<LocalAudioTrack | null>(null);
  // CORE-737 — the practice clip, published for the recorder only.
  const practiceVideoTrackRef = useRef<LocalVideoTrack | null>(null);
  const practiceAudioTrackRef = useRef<LocalAudioTrack | null>(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenShareLoading, setScreenShareLoading] = useState(false);
  const [, force] = useState(0);

  const attachLocalVideo = useCallback((container: HTMLElement | null) => {
    if (!container) return;
    containerRef.current = container;
    const track = videoTrackRef.current;
    if (!track) return;
    let v = container.querySelector('video') as HTMLVideoElement | null;
    if (!v) {
      v = document.createElement('video');
      v.autoplay = true;
      v.playsInline = true;
      v.muted = true;
      v.style.width = '100%';
      v.style.height = '100%';
      v.style.objectFit = 'cover';
      container.replaceChildren(v);
    }
    applySelfViewMirror(v); // CORE-527 — one image for master, viewers and recording.
    track.attach(v);
  }, []);

  const wireMicLevel = useCallback((stream: MediaStream) => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      let raf = 0;
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i] - 128) / 128);
        micLevelRef.current = peak;
        raf = requestAnimationFrame(tick);
      };
      tick();
      analyserCleanupRef.current = () => {
        cancelAnimationFrame(raf);
        ctx.close().catch(() => {});
      };
    } catch {
      /* level meter is best-effort */
    }
  }, []);

  const startPreview = useCallback(
    async (container: HTMLElement | null, opts?: { audioMode?: 'voice' | 'music'; micDeviceId?: string; camDeviceId?: string }) => {
      const audioConstraints: MediaTrackConstraints = opts?.micDeviceId
        ? { deviceId: { exact: opts.micDeviceId } }
        : {};
      if (opts?.audioMode !== 'music') {
        audioConstraints.echoCancellation = true;
        audioConstraints.noiseSuppression = true;
        audioConstraints.autoGainControl = true;
      }
      const videoConstraints: MediaTrackConstraints = opts?.camDeviceId
        ? { deviceId: { exact: opts.camDeviceId } }
        : {};

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: videoConstraints,
      });
      const [videoMst] = stream.getVideoTracks();
      const [audioMst] = stream.getAudioTracks();
      const lk = await loadLiveKit();
      if (videoMst) videoTrackRef.current = new lk.LocalVideoTrack(videoMst);
      if (audioMst) audioTrackRef.current = new lk.LocalAudioTrack(audioMst);
      wireMicLevel(stream);
      if (container) attachLocalVideo(container);
      force(n => n + 1);
    },
    [attachLocalVideo, wireMicLevel],
  );

  const goLive = useCallback(async (token: AgoraTokenResponse, opts?: { onRoom?: (room: Room) => void }) => {
    if (!token.url) throw new Error('LiveKit token missing url');
    const lk = await loadLiveKit();
    const room = new lk.Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;
    opts?.onRoom?.(room);
    await room.connect(token.url, token.token);
    if (videoTrackRef.current) {
      await room.localParticipant.publishTrack(videoTrackRef.current, { source: LkTrackSource.Camera });
    }
    if (audioTrackRef.current) {
      await room.localParticipant.publishTrack(audioTrackRef.current, { source: LkTrackSource.Microphone });
    }
    if (containerRef.current) attachLocalVideo(containerRef.current);
  }, [attachLocalVideo]);

  const setMicEnabled = useCallback(async (on: boolean) => {
    const t = audioTrackRef.current;
    if (!t) return;
    if (on) await t.unmute();
    else await t.mute();
  }, []);

  const setCamEnabled = useCallback(async (on: boolean) => {
    const t = videoTrackRef.current;
    if (!t) return;
    if (on) await t.unmute();
    else await t.mute();
  }, []);

  const stopScreenShare = useCallback(async () => {
    const room = roomRef.current;
    const sv = screenVideoTrackRef.current;
    const sa = screenAudioTrackRef.current;
    if (room && sv) { try { await room.localParticipant.unpublishTrack(sv); } catch { /* ignore */ } }
    if (room && sa) { try { await room.localParticipant.unpublishTrack(sa); } catch { /* ignore */ } }
    try { sv?.stop(); } catch { /* ignore */ }
    try { sa?.stop(); } catch { /* ignore */ }
    screenVideoTrackRef.current = null;
    screenAudioTrackRef.current = null;
    if (liveId != null) {
      try { await api.setLiveScreenShareState(liveId, false); }
      catch (e) { console.warn('setLiveScreenShareState(false) failed', e); }
    }
    setIsScreenSharing(false);
  }, [liveId]);

  const startScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) throw new Error('Screen-share requires an active broadcast');
    const display = await (navigator.mediaDevices as any).getDisplayMedia({ video: true, audio: true });
    const [screenMst] = display.getVideoTracks();
    const [screenAudioMst] = display.getAudioTracks();
    if (!screenMst) throw new Error('No screen video track');
    const lk = await loadLiveKit();
    const screenVideo = new lk.LocalVideoTrack(screenMst);
    const screenAudio = screenAudioMst ? new lk.LocalAudioTrack(screenAudioMst) : null;
    // Browser's native "Stop sharing" plate → flip our UI + teardown.
    screenMst.addEventListener('ended', () => {
      stopScreenShare().catch(err => console.warn('auto stopScreenShare failed', err));
    });
    try {
      await room.localParticipant.publishTrack(screenVideo, { source: LkTrackSource.ScreenShare });
      if (screenAudio) {
        await room.localParticipant.publishTrack(screenAudio, { source: LkTrackSource.ScreenShareAudio });
      }
    } catch (e) {
      try { screenVideo.stop(); } catch { /* ignore */ }
      try { screenAudio?.stop(); } catch { /* ignore */ }
      throw e;
    }
    screenVideoTrackRef.current = screenVideo;
    screenAudioTrackRef.current = screenAudio;
    if (liveId != null) {
      try { await api.setLiveScreenShareState(liveId, true); }
      catch (e) { console.warn('setLiveScreenShareState(true) failed', e); }
    }
    setIsScreenSharing(true);
  }, [liveId, stopScreenShare]);

  const toggleScreenShare = useCallback(async () => {
    if (screenShareLoading) return;
    setScreenShareLoading(true);
    try {
      if (isScreenSharing) await stopScreenShare();
      else await startScreenShare();
    } finally {
      setScreenShareLoading(false);
    }
  }, [isScreenSharing, screenShareLoading, startScreenShare, stopScreenShare]);

  const stop = useCallback(() => {
    analyserCleanupRef.current?.();
    analyserCleanupRef.current = null;
    try { videoTrackRef.current?.stop(); } catch { /* ignore */ }
    try { audioTrackRef.current?.stop(); } catch { /* ignore */ }
    try { screenVideoTrackRef.current?.stop(); } catch { /* ignore */ }
    try { screenAudioTrackRef.current?.stop(); } catch { /* ignore */ }
    videoTrackRef.current = null;
    audioTrackRef.current = null;
    screenVideoTrackRef.current = null;
    screenAudioTrackRef.current = null;
    if (roomRef.current) {
      roomRef.current.disconnect().catch(() => {});
      roomRef.current = null;
    }
    micLevelRef.current = 0;
  }, []);


  /**
   * CORE-737 — the practice is not otherwise in the room: every client plays its
   * own <video> synced over SignalR, and room-composite egress records the ROOM,
   * so the archive had the master talking over nothing. Publishing the captured
   * element here puts it in front of the recorder. Viewers skip it by name.
   */
  const unpublishPractice = useCallback(async () => {
    const room = roomRef.current;
    const v = practiceVideoTrackRef.current;
    const a = practiceAudioTrackRef.current;
    practiceVideoTrackRef.current = null;
    practiceAudioTrackRef.current = null;
    if (room && v) { try { await room.localParticipant.unpublishTrack(v); } catch { /* already gone */ } }
    if (room && a) { try { await room.localParticipant.unpublishTrack(a); } catch { /* already gone */ } }
    try { v?.stop(); } catch { /* already stopped */ }
    try { a?.stop(); } catch { /* already stopped */ }
  }, []);

  const publishPractice = useCallback(async (video: MediaStreamTrack | null, audio: MediaStreamTrack | null) => {
    const room = roomRef.current;
    if (!room || !video) return;
    // Republishing over a live publication would leave the old one in the room —
    // a second, frozen practice tile in the recording.
    // Комната есть — значит, пакет уже загружен (`goLive`).
    await unpublishPractice();
    const v = new (liveKitLoaded().LocalVideoTrack)(video);
    practiceVideoTrackRef.current = v;
    await room.localParticipant.publishTrack(v, { name: PRACTICE_TRACK_NAME, source: LkTrackSource.Unknown });
    if (audio) {
      const a = new (liveKitLoaded().LocalAudioTrack)(audio);
      practiceAudioTrackRef.current = a;
      await room.localParticipant.publishTrack(a, { name: PRACTICE_TRACK_NAME, source: LkTrackSource.Unknown });
    }
  }, [unpublishPractice]);

  return {
    startPreview,
    goLive,
    setMicEnabled,
    setCamEnabled,
    attachLocal: attachLocalVideo,
    micLevelRef,
    isScreenSharing,
    screenShareLoading,
    toggleScreenShare,
    stopScreenShare,
    publishPractice,
    unpublishPractice,
    stop,
  };
}
